import DSGProductCarouselProduct from "@DSG/Components/DSGProductCarouselProduct";

import colorPallete, {
    bodyFonts,
    buttonFonts,
    elevation,
    headlineFonts,
    themeFontWeights,
} from "@DSG/MobiusOverrides/styles/variables";

import Carousel, { CarouselSlide } from "@DSG/Overrides/Components/Carousel";

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendImpressionTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendImpressionTracking";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductCarouselProductStyles } from "@insite/content-library/Components/ProductCarouselProduct";
import { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps, StyledButton } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { createRef, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    productNumbers = "productNumbers",
    widgetPosition = "widgetPosition",
    showImageBadges = "showImageBadges",
    customCSS = "customCSS",
    titleAlignment = "titleAlignment",
    showImage = "showImage",
    showBrand = "showBrand",
    showTitle = "showTitle",
    showAddToList = "showAddToList",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
    showViewProduct = "showViewProduct",
    viewAllLinkUrl = "viewAllLinkUrl",
    viewAllLinkText = "viewAllLinkText",
    showProductNumber = "showProductNumber",
    showQuantityBox = "showQuantityBox",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.widgetPosition]: number;
        [fields.showImageBadges]: boolean;
        [fields.customCSS]: string;
        [fields.titleAlignment]: string;
        [fields.showImage]: boolean;
        [fields.showBrand]: boolean;
        [fields.showTitle]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
        [fields.showViewProduct]: boolean;
        [fields.productNumbers]: string[];
        [fields.viewAllLinkUrl]: LinkFieldValue;
        [fields.viewAllLinkText]: string;
        [fields.showProductNumber]: boolean;
        [fields.showQuantityBox]: boolean;
    };
}

export interface DSGProductSpotlightCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    sectionContentWrapper: InjectableCss;
    brandProductCarouselContainer?: InjectableCss;
    contentWrapper: InjectableCss;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    products: getProductsForProductInfoList(state, ownProps.id),
    errorMessage: getErrorMessage(state, ownProps.id),
    pageType: getCurrentPage(state).type,
    productRecommendationWidgets: state.components.productRecommendations.widgets,
    session: state.context.session,
});

const mapDispatchToProps = {
    loadCarouselProducts,
    sendImpressionTracking,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export const DSGProductSpotlightCarouselStyles: DSGProductSpotlightCarouselStyles = {
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        forwardAs: "h2",
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 24px;

            @media only screen and (max-width: 768px) {
                margin-bottom: 26px;
            }
        `,
    },
    sectionContentWrapper: {
        css: css`
            justify-content: center;
        `,
    },
    brandProductCarouselContainer: {
        css: css`
            padding: 48px 0;

            &:empty {
                display: none;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding: 24px 0;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    contentWrapper: {
        css: css`
            display: block;
            width: 100%;
            padding-bottom: 48px;
            max-width: 1280px;
            margin: 0 auto;

            @media only screen and (max-width: 768px) {
                padding-bottom: 40px;
            }

            .spotlight {
                &-rte {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0 24px;
                    @media only screen and (max-width: 1024px) {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                }

                &-title {
                    font-size: ${headlineFonts.headline2SemiBoldLg.fontSize};
                    line-height: ${headlineFonts.headline2SemiBoldLg.lineHeight};
                    font-weight: ${headlineFonts.headline2SemiBoldLg.fontWeight};
                    color: ${colorPallete.grey800};
                    @media only screen and (max-width: 768px) {
                        font-size: ${headlineFonts.headline2SemiBoldLg.fontSize};
                        line-height: ${headlineFonts.headline2SemiBoldLg.lineHeight};
                        text-align: left;
                    }
                    @media only screen and (max-width: 1024px) {
                        margin-bottom: 8px;
                    }
                }

                &-description,
                &-description p {
                    font-size: ${bodyFonts.body1RegularLg.fontSize};
                    line-height: ${bodyFonts.body1RegularLg.lineHeight};
                    font-weight: ${bodyFonts.body1RegularLg.fontWeight};
                    text-align: left;
                    @media only screen and (max-width: 768px) {
                        font-size: ${bodyFonts.body1RegularSm.fontSize};
                        line-height: ${bodyFonts.body1RegularSm.lineHeight};
                    }
                    @media only screen and (max-width: 1024px) {
                        margin-bottom: 16px;
                    }
                }
                &-link {
                    flex-shrink: 0;
                }
            }
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            max-width: 1280px !important;
            margin: 0 auto;

            [class*="GridOffset-"] {
                @media only screen and (max-width: 768px) {
                    justify-content: flex-end;
                }

                .btn-secondary {
                    margin-bottom: 4px;
                }
            }
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
            @media only screen and (max-width: 768px) {
                display: none;
            }
        `,
    },
    prevArrowButton: {
        shape: "rounded",
        css: css`
            padding: 0;
            min-width: 40px;
            border: 1px solid ${colorPallete.grey200};
            background: ${colorPallete.white};
            color: ${colorPallete.grey800};
            z-index: 2;
            box-shadow: ${elevation.dp04};

            [class*="ButtonIcon-"] {
                color: ${colorPallete.grey800};
            }

            &:not(:disabled):hover {
                background: ${colorPallete.white};
                color: ${colorPallete.black};
                border: 1px solid ${colorPallete.grey100};

                [class*="ButtonIcon-"] {
                    color: ${colorPallete.black};
                }
            }
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
            @media only screen and (max-width: 768px) {
                display: none;
            }
        `,
    },
    nextArrowButton: {
        shape: "rounded",
        css: css`
            padding: 0;
            min-width: 40px;
            border: 1px solid ${colorPallete.grey200};
            background: ${colorPallete.white};
            color: ${colorPallete.grey800};
            z-index: 2;
            box-shadow: ${elevation.dp04};

            [class*="ButtonIcon-"] {
                color: ${colorPallete.grey800};
            }

            &:not(:disabled):hover {
                background: ${colorPallete.white};
                color: ${colorPallete.black};
                border: 1px solid ${colorPallete.grey100};

                [class*="ButtonIcon-"] {
                    color: ${colorPallete.black};
                }
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            padding: 0 0 8px 0;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;

            @media only screen and (min-width: 767px) {
                justify-content: flex-start;
            }

            @media only screen and (min-width: 1024px) {
                justify-content: center;
            }
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            background: ${colorPallete.white};
            flex: 0 0 243px;
            max-width: 243px;
            margin-left: 16px;

            @media only screen and (min-width: 1220px) {
                &:not(:first-child) {
                    margin-left: 40px;
                }
            }

            @media (max-width: 1024px) {
                max-width: 220px;
                flex: 0 0 220px;
            }

            @media only screen and (max-width: 768px) {
                flex: 0 0 225px;
                max-width: 225px;
            }
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 8px 0;
            padding-bottom: 16px;
            display: flex;
            flex-direction: column;

            [data-test-selector="productImage"] {
                padding: 16px;
            }
            > div {
                display: flex;
                flex-direction: column;
                height: 100%;
            }
        `,
    },
    carouselProductStyles: {
        productPriceWrapper: {
            css: css`
                padding: 0 16px;

                &.signedIn {
                    margin-bottom: 16px;
                }

                &.notSignedIn {
                    .signInWrapperPrice {
                        margin-bottom: 0;

                        > ${StyledButton} {
                            font-size: ${buttonFonts.buttonSm.fontSize};
                            line-height: ${buttonFonts.buttonSm.lineHeight};
                        }

                        @media only screen and (min-width: 1120px) {
                            > ${StyledButton} {
                                font-size: ${buttonFonts.buttonLg.fontSize};
                                line-height: ${buttonFonts.buttonLg.lineHeight};
                            }
                        }
                    }
                }

                .price {
                    font-size: ${headlineFonts.headline2SemiBoldLg.fontSize};
                    line-height: ${headlineFonts.headline3SemiBoldLg.lineHeight};
                }

                .uom {
                    font-size: ${bodyFonts.body2RegularLg.fontSize};
                    line-height: ${bodyFonts.body2RegularLg.lineHeight};
                    font-weight: ${bodyFonts.body2RegularLg.fontWeight};
                }
            `,
        },
        productDescriptionStyles: {
            productDetailLink: {
                css: css`
                    margin-bottom: 16px;
                `,
            },
            productDetailDescription: {
                css: css`
                    margin-bottom: 16px;
                `,
            },
        },
        productPartNumbersStyles: {
            container: {
                css: css`
                    padding: 0 16px;
                    margin-bottom: 16px;
                `,
            },
        },
        addToCartContainer: {
            offsetCss: css`
                display: block;
            `,
            css: css`
                padding: 0 16px;

                > ${GridOffset} {
                    width: 100%;
                    margin: 0;
                    display: block;

                    > ${GridItemStyle} {
                        padding: 0;
                        flex-basis: 100%;
                        max-width: 100%;
                    }
                }
            `,
        },
    },
};

const defaultCustomCss = `.wrapper {
    }
    
    .titleText {
    }
    `;

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const DsgBrandProductCarousel = ({ id, fields, products, loadCarouselProducts, shellContext }: Props) => {
    const styles = DSGProductSpotlightCarouselStyles;
    const afterCarousel = createRef<HTMLSpanElement>();

    const getTabsProductNumber = (productNumbers: any[]) => {
        if (!productNumbers || productNumbers.length === 0) {
            return;
        }
        const productsSpotlight = [] as string[];
        productNumbers.forEach(r => {
            productsSpotlight.push(r.fields.productNumbers);
        });
        return productsSpotlight;
    };
    const [widgetTitle, setTitle] = useState(fields.title);

    const productNumbers = getTabsProductNumber(fields.productNumbers) as string[] | undefined;

    const [carouselProducts, setCarouselProducts] = useState(productNumbers);

    const mapProductsToSlides = (products: ProductModel[]) =>
        products.map(product => ({
            id: product.id,
            renderComponent: function DSGProductCarouselProductRenderer() {
                return (
                    <div>
                        <DSGProductCarouselProduct
                            carouselId={id}
                            key={product.id}
                            product={product}
                            showImage={fields.showImage}
                            showBrand={fields.showBrand}
                            showTitle={fields.showTitle}
                            showPartNumbers={fields.showProductNumber}
                            showPrice={fields.showPrice}
                            showAddToCart={fields.showAddToCart}
                            showAddToList={fields.showAddToList}
                            showImageBadges={fields.showImageBadges}
                            maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                            showTextBadges={fields.showTextBadges}
                            maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                            extendedStyles={styles.carouselProductStyles}
                            showViewProduct={fields.showViewProduct}
                            showQtyBox={fields.showQuantityBox}
                        />
                    </div>
                );
            },
        }));

    const [slides, setSlides] = useState<CarouselSlide[]>(products ? mapProductsToSlides(products) : []);
    useEffect(() => {
        if (!products || products.length === 0) {
            return;
        }

        setSlides(mapProductsToSlides(products));
    }, [products]);

    useEffect(() => {
        loadCarouselProducts({
            isShellEdit: shellContext.isInShell && shellContext.isEditing,
            carouselId: id,
            carouselType: `BrandProductCarousel|${widgetTitle}`,
            productNumbers,
        } as any);
    }, []);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...styles.brandProductCarouselContainer}>
            {fields.title !== "" && (
                <ContentWrapper alignment={fields.titleAlignment} className="wrapper" {...styles.sectionContentWrapper}>
                    <Typography {...styles.titleText}>{widgetTitle}</Typography>
                </ContentWrapper>
            )}
            {slides && slides.length > 0 && (
                <StyledWrapper className="tab-button-wrapper">
                    <Carousel
                        id="Tab5"
                        maxNumberOfColumns={4}
                        title=""
                        slides={slides}
                        extendedStyles={DSGProductSpotlightCarouselStyles}
                    />
                </StyledWrapper>
            )}
            {fields.viewAllLinkText !== "" && (
                <ContentWrapper alignment="center">
                    <Clickable href={useGetLink(fields.viewAllLinkUrl).url} className="text-link text-link-primary">
                        {parse(fields.viewAllLinkText, parserOptions)}
                    </Clickable>
                </ContentWrapper>
            )}
        </StyledWrapper>
    );
};

export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: flex;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    ${injectCss};
`;

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(DsgBrandProductCarousel))),
    definition: {
        group: "Common",
        icon: "gallery-thumbnails",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "Contextual",
                sortOrder: 1,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showViewProduct,
                displayName: "View Product",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 2,
            },
            {
                name: fields.showPrice,
                displayName: "Show Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
                sortOrder: 4,
            },
            {
                name: fields.showProductNumber,
                displayName: "Show Product Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showQuantityBox,
                displayName: "Show Quantity Box",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "Contextual",
                sortOrder: 7,
            },
            {
                name: fields.showBrand,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 8,
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 9,
            },
            {
                name: fields.showTextBadges,
                displayName: "Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 10,
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 11,
            },
            {
                name: fields.productNumbers,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.productNumbers;
                },
                defaultValue: [{ fields: { name: "" } }],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "productNumbers",
                        displayName: "",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
                sortOrder: 12,
            },
            {
                fieldType: "Contextual",
                name: fields.viewAllLinkUrl,
                displayName: "View All Link Url",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                sortOrder: 13,
            },
            {
                name: fields.viewAllLinkText,
                displayName: "View All Link Text",
                editorTemplate: "TextField",
                defaultValue: "View All",
                fieldType: "Translatable",
                sortOrder: 14,
            },
        ],
    },
};

export default widgetModule;